/**
 * useOrgDesignData.js
 *
 * Every Org Design screen reads/writes through these hooks instead of
 * importing useOrgDesignStore directly — same convention as
 * TalentMobilityV2/useTalentMobilityData.js. Swapping in a real Org Context
 * file API later means changing the body of these hooks only, not the
 * screens that consume them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SbNotification } from "uiCore";
import useAuthStore from "Store/useAuthStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import useOrgDesignStore from "Store/useOrgDesignStore";
import { decodeToken } from "Components/SbSessionCheckerComponent/SbSessionChecker";
import {
  getOrgRedesignCurrentOrgStructure,
  getOrgRedesignOrganizationView,
  createOrgRedesignVersion,
  getOrgRedesignVersion,
  getOrgRedesignVersions,
  updateOrgRedesignVersion,
  updateOrgRedesignArchiveStatus,
  getOrgRedesignVersionBudget,
  getCurrentOrgBudget,
  getRoleReportingView,
} from "Api/apiService";
import {
  buildOrgDesignResult, computeBudget, newRoleNodeId, findUnreachableRoleIds, findRolesReportingToProposedRoles,
} from "./orgDesignTreeEngine";
import { mapCurrentOrgStructureRole, adaptVersionRowToDraft, resolveOrgViewRootIds } from "./orgDesignAdapters";
import {
  ENTRY_OPTION,
  ORG_REDESIGN_VERSION_NOT_FOUND_ERROR_CODE,
  ORG_REDESIGN_ARCHIVE_STATUS,
  getOrgRedesignSaveErrorMessage,
  getOrgRedesignArchiveErrorMessage,
  excludeArchivedVersions,
} from "./orgDesign.constants";

export { mapCurrentOrgStructureRole, mapOrgRedesignVersionRow, adaptVersionRowToDraft } from "./orgDesignAdapters";

// Org Design's own entitlement resource (not people_plus_ai — PAIC is only a
// data source it consumes, not its access gate). view_comp specifically
// gates budget/compensation visibility (HR Manager has it, HR Executive
// doesn't) — never checked by role name. One hook, so every screen/component
// reads the resulting boolean rather than re-deriving it.
export const useCanViewOrgDesignBudget = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const orgRedesignEntitlements = entitlements?.find((e) => e.name === "org_redesign")?.entitlements;
  return !!orgRedesignEntitlements?.includes("view_comp");
};

// "Start from current org" reads the live reporting structure
// (getOrgRedesignCurrentOrgStructure -> GET /current-org-structure), which
// backend gates on role_reporting_to:view rather than org_redesign — a
// separate resource from the rest of this module. Checked up front so the
// entry card reflects real access instead of the user hitting a 403 after
// clicking it.
export const useCanAccessCurrentOrgFlow = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const roleReportingToEntitlements = entitlements?.find((e) => e.name === "role_reporting_to")?.entitlements;
  return !!roleReportingToEntitlements?.includes("view");
};

// "Start from a PAIC recommended org" browses PAIC projects/groups/versions
// before Org Design ever generates anything — gated on people_plus_ai:view,
// the same resource/entitlement that gates the People+AI module itself,
// since this is PAIC data being browsed, not an Org Design resource.
export const useCanAccessPaicOrgDesignFlow = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const peoplePlusAiEntitlements = entitlements?.find((e) => e.name === "people_plus_ai")?.entitlements;
  return !!peoplePlusAiEntitlements?.includes("view");
};

// POST /create-org-redesign-version (Generate + first Save Version) is
// gated on org_redesign:create; PUT /version/{id}/update (every later save)
// on org_redesign:edit — the actual entitlement name the backend returns
// for HR Manager/Executive, not "update" — distinct from plain
// org_redesign:view, which only gets a user into the module/route itself.
export const useCanCreateOrgRedesign = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const orgRedesignEntitlements = entitlements?.find((e) => e.name === "org_redesign")?.entitlements;
  return !!orgRedesignEntitlements?.includes("create");
};

export const useCanUpdateOrgRedesign = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const orgRedesignEntitlements = entitlements?.find((e) => e.name === "org_redesign")?.entitlements;
  return !!orgRedesignEntitlements?.includes("edit");
};

// Backend gates PUT /version/{id}/update_archive_status on
// org_redesign:delete. Archiving keeps the data; it only hides the version
// from the list.
export const useCanArchiveOrgRedesign = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const orgRedesignEntitlements = entitlements?.find((e) => e.name === "org_redesign")?.entitlements;
  return !!orgRedesignEntitlements?.includes("delete");
};

// The signed-in user's sb_user_unique_id, read from the JWT the same way
// useModuleTour does. Used only to decide which rows show owner-only
// actions; the backend makes the real ownership check.
export const useCurrentSbUserId = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  return useMemo(() => decodeToken(accessToken)?.sb_user_unique_id ?? null, [accessToken]);
};

// Role Reporting rows backing the extra "Reports To" choices — fetched once
// per workspace mount. On failure the list stays empty, so the dropdowns
// fall back to this design's own roles, same as before.
export const useRoleReportingRows = () => {
  const email = useAuthStore((state) => state.email);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!email) return undefined;
    let active = true;
    getRoleReportingView(email)
      .then((response) => {
        if (active) setRows(Array.isArray(response) ? response : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [email]);
  return rows;
};

// role_title alone isn't a unique key — two proposed roles can share a title.
const generateNewRoleId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `newrole-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// A CURRENT ORG-sourced version gets the same real-root tree a fresh "Start
// from Current Org" does (see startFromExistingOrg) — organization-view has
// no version-scoped variant, it's always today's live org, but the true
// top-of-org role(s) essentially never change between when a version was
// captured and now, so it's a safe source for root identification here too.
// PAIC has no equivalent endpoint, so a PAIC row skips the fetch and roots
// on its own manager-less roles instead.
const fetchOrgRedesignVersionDraft = async (orgRedesignId, email, signal) => {
  const row = await getOrgRedesignVersion(orgRedesignId, signal);
  const view =
    row?.source_type === "CURRENT ORG"
      ? await getOrgRedesignOrganizationView(email, signal).catch((viewError) => {
          console.error("Error getting organization view:", viewError);
          return null;
        })
      : null;
  return adaptVersionRowToDraft(row, view);
};

// "<name> — Version N" for a GET /versions, create or update row — the
// typed org_redesign_name wins over the PAIC group's project_name.
// `fallbackName` covers a response that omits both.
const buildVersionLabel = (row, fallbackName) => {
  const name = row.org_redesign_name || row.project_name || fallbackName;
  return `${name ? `${name} — ` : ""}Version ${row.version}`;
};

// Local/session-scoped saves — used by usePaicOrgDesignLookup's "already
// generated this run?" check and instant same-session reopen. The landing
// page's own "Previous Iterations" list reads GET /versions instead (see
// usePublishedOrgRedesignVersions).
export const useOrgDesignVersions = () => {
  const versions = useOrgDesignStore((state) => state.savedVersions);
  return { versions };
};

// Org-wide, cross-session list of every PUBLISHED version. Rows are
// lightweight (no roles/trees), so opening one fetches the full detail on
// demand and adapts it the same way "Generate Org Design" does.
export const usePublishedOrgRedesignVersions = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  // A ref as well as state, so a double click can't start a second request
  // before the re-render that disables the button.
  const archivingRef = useRef(false);
  const setDraft = useOrgDesignStore((state) => state.setDraft);
  const email = useAuthStore((state) => state.email);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await getOrgRedesignVersions();
        setVersions(excludeArchivedVersions(response));
      } catch (error) {
        // Backend returns this instead of an empty array when nothing has
        // been published yet — not a real failure, so it renders as the
        // normal "no versions" empty state rather than an error toast.
        if (error?.response?.data?.error_code !== ORG_REDESIGN_VERSION_NOT_FOUND_ERROR_CODE) {
          SbNotification.error("Unable to load published Organisation Designs", "Please try again.");
        }
        setVersions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openVersion = useCallback(
    async (row) => {
      setOpening(true);
      const loadToken = useOrgDesignStore.getState().claimLoad();
      try {
        const draft = await fetchOrgRedesignVersionDraft(row.sb_org_redesign_unique_id, email);
        if (!useOrgDesignStore.getState().isActiveLoad(loadToken)) return false;
        setDraft({
          ...draft,
          entryOption: row.source_type === "PAIC" ? ENTRY_OPTION.PAIC_RECOMMENDED : ENTRY_OPTION.EXISTING_ORG,
          sourceLabel: buildVersionLabel(row),
          sbSelectionUniqueId: null,
          baseVersionId: row.sb_org_redesign_unique_id,
          orgRedesignId: row.sb_org_redesign_unique_id,
          targetBudget: null,
          sessionId: loadToken,
        });
        return true;
      } catch (error) {
        if (useOrgDesignStore.getState().isActiveLoad(loadToken)) {
          SbNotification.error("Unable to open Organisation Design version", "Please try again.");
        }
        return false;
      } finally {
        setOpening(false);
      }
    },
    [setDraft, email]
  );

  // Resolves true once the version is archived. The row is then dropped
  // locally, since GET /versions would no longer return it anyway. If the
  // persisted workspace draft is this same version, it's cleared too, so the
  // workspace can't keep editing and saving an archived version.
  const archiveVersion = useCallback(async (row) => {
    const orgRedesignId = row?.sb_org_redesign_unique_id;
    if (!orgRedesignId || archivingRef.current) return false;
    archivingRef.current = true;
    setArchivingId(orgRedesignId);
    try {
      await updateOrgRedesignArchiveStatus(orgRedesignId, ORG_REDESIGN_ARCHIVE_STATUS.ARCHIVED);
      setVersions((prev) => prev.filter((v) => v.sb_org_redesign_unique_id !== orgRedesignId));
      const { draft, setDraft: setStoreDraft } = useOrgDesignStore.getState();
      if (draft?.orgRedesignId === orgRedesignId) setStoreDraft(null);
      const name = row.org_redesign_name || row.project_name;
      SbNotification.success(
        "Organisation Design Archived",
        name ? `"${name}" has been archived.` : "The Organisation Design has been archived."
      );
      return true;
    } catch (error) {
      SbNotification.error("Archive Failed", getOrgRedesignArchiveErrorMessage(error));
      return false;
    } finally {
      archivingRef.current = false;
      setArchivingId(null);
    }
  }, []);

  return { versions, loading, opening, openVersion, archivingId, archiveVersion };
};

// Only entries draft.design actually touched are sent as overrides.
const buildExistingRoleBudgetOverrides = (draft) =>
  Object.entries(draft.design || {})
    .filter(([, v]) => v?.after_hc != null)
    .map(([sb_unique_id, v]) => ({ sb_unique_id, total_positions: v.after_hc }));

// POST /version/{id}/budget matches new_roles by title only — it already
// has each role's cost from new_org_structure.new_roles.
const buildNewRoleBudgetOverridesForVersion = (draft) =>
  (draft.newRoles || []).map((nr) => ({ role_title: nr.role_title, total_positions: nr.after_hc }));

// POST /current-org-budget has no persisted new_roles to look compensation
// up against, so the caller supplies it directly.
const buildNewRoleBudgetOverridesForCurrentOrg = (draft) =>
  (draft.newRoles || []).map((nr) => ({
    role_title: nr.role_title,
    compensation: nr.annual_cost_usd_per_fte,
    total_positions: nr.after_hc,
  }));

// PUT /version/{id}/update — every role touched (headcount, status, or a
// pure reporting-line move; see updateReportingLine) is sent as its full
// current state rather than a partial diff, so the backend's per-field merge
// semantics never come into play.
const buildExistingRoleUpdates = (draft) =>
  Object.keys(draft.design || {}).map((roleId) => {
    const role = draft.roles.find((r) => r.sb_unique_id === roleId);
    const design = draft.design[roleId] || {};
    return {
      sb_unique_id: roleId,
      reports_to: role?.manager_id || null,
      status: design.status || "UNCHANGED",
      target_positions: design.after_hc != null ? design.after_hc : role?.total_positions ?? 0,
    };
  });

const buildNewRoleUpdates = (draft) =>
  (draft.newRoles || []).map((nr) => ({
    role_title: nr.role_title,
    reports_to: nr.manager_id || null,
    target_positions: nr.after_hc,
    annual_cost_usd_per_fte: nr.annual_cost_usd_per_fte,
    rationale: nr.rationale || "",
    status: nr.status || "CREATED",
  }));

// Every reparent rewrites all four manager fields together, so a previous
// manager's job_id/title can never linger. `externalManager` is the picked
// Role Reporting option when the manager isn't one of this design's roles.
const buildManagerFields = (draft, managerId, externalManager) => {
  const manager = managerId ? externalManager || draft.roles.find((r) => r.sb_unique_id === managerId) : null;
  return {
    manager_id: managerId,
    manager_job_id: manager?.job_id ?? null,
    manager_job_title: manager?.job_title ?? null,
    manager_is_external: Boolean(managerId && externalManager),
  };
};

// orgRedesignId (a persisted version) takes priority over entryOption — a
// draft tied to one specific saved version must always price against that
// version, not the live org, even if the live org has since changed.
// Returns null when neither applies, so callers just keep the current figures.
const fetchServerBudget = async (draft, signal) => {
  const payload = { existing_roles: buildExistingRoleBudgetOverrides(draft) };
  if (draft.targetBudget != null) payload.target_budget = draft.targetBudget;

  if (draft.orgRedesignId) {
    payload.new_roles = buildNewRoleBudgetOverridesForVersion(draft);
    return getOrgRedesignVersionBudget(draft.orgRedesignId, payload, signal);
  }
  if (draft.entryOption === ENTRY_OPTION.EXISTING_ORG) {
    payload.new_roles = buildNewRoleBudgetOverridesForCurrentOrg(draft);
    return getCurrentOrgBudget(payload, signal);
  }
  return null;
};

export const useOrgDesignWorkspace = () => {
  const draft = useOrgDesignStore((state) => state.draft);
  const setDraft = useOrgDesignStore((state) => state.setDraft);
  const saveDraftAsVersion = useOrgDesignStore((state) => state.saveDraftAsVersion);
  const email = useAuthStore((state) => state.email);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const canViewBudget = useCanViewOrgDesignBudget();
  const canCreateOrgRedesign = useCanCreateOrgRedesign();
  const canUpdateOrgRedesign = useCanUpdateOrgRedesign();
  const [startingExistingOrg, setStartingExistingOrg] = useState(false);
  const [budgetSyncing, setBudgetSyncing] = useState(false);
  const budgetSyncTimerRef = useRef(null);
  const budgetSyncAbortRef = useRef(null);
  // Bumped on every scheduleBudgetSync call — lets a fetch that's already in
  // flight recognize a newer call superseded it, even though sessionId (same
  // draft) hasn't changed. Without this, a slower request from an earlier
  // edit could resolve after a faster one from a later edit and overwrite
  // its fresher numbers.
  const budgetSyncTokenRef = useRef(0);

  // Timer/in-flight request aren't tied to any component's render — clear
  // them on unmount so work scheduled right before the user navigates away
  // never fires or lands later.
  useEffect(
    () => () => {
      clearTimeout(budgetSyncTimerRef.current);
      budgetSyncAbortRef.current?.abort();
    },
    []
  );

  // Debounced so a rapid run of edits fires one budget call, not one per
  // keystroke. Reads/writes the store directly rather than the draft this
  // closure captured, so a response landing after further edits merges into
  // whatever is current instead of clobbering it — but only if `current` is
  // still the same draft session (`sessionId`, stamped by whichever
  // start*/open* action created it and preserved through every recompute):
  // without that check, a sync scheduled for one draft could land on a
  // completely different one loaded in the meantime. `setBudgetSyncing(true)`
  // fires synchronously (not inside the timeout) so the Budget Bar hides the
  // stale local number for the whole debounce-plus-request window, not just
  // once the request starts — avoids a flicker right after the edit.
  // `immediate` skips the debounce — used for the one-time sync on mount,
  // where there's no rapid edit burst to protect against, so the extra
  // 600ms is just added wait before Role Count/Budget Bar have any data.
  const scheduleBudgetSync = useCallback((latestDraft, { immediate = false } = {}) => {
    if (budgetSyncTimerRef.current) clearTimeout(budgetSyncTimerRef.current);
    budgetSyncAbortRef.current?.abort();
    setBudgetSyncing(true);
    const sessionId = latestDraft.sessionId;
    const token = ++budgetSyncTokenRef.current;
    budgetSyncTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      budgetSyncAbortRef.current = controller;
      try {
        const budget = await fetchServerBudget(latestDraft, controller.signal);
        if (!budget) return;
        // A newer scheduleBudgetSync call has since started — even though
        // it's the same draft session, this response is from a superseded
        // edit and would overwrite fresher numbers if applied.
        if (budgetSyncTokenRef.current !== token) return;
        const current = useOrgDesignStore.getState().draft;
        if (!current || current.sessionId !== sessionId) return;
        useOrgDesignStore.setState({
          draft: {
            ...current,
            baseline_budget: budget.baseline_budget,
            projected_budget: budget.projected_budget,
            budgetWithinTarget: budget.within_budget,
            // Role-count fields are only present on some budget response
            // variants — kept as-is (possibly undefined per field) so the
            // UI can tell "not returned" apart from a real 0 and render
            // nothing for whichever ones are missing, rather than guessing.
            roleCounts: {
              baseline: budget.baseline_role_count,
              added: budget.added_role_count,
              augmented: budget.augmented_role_count,
              reduced: budget.reduced_role_count,
              redundant: budget.redundant_role_count,
            },
          },
        });
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Error syncing budget from server:", error);
      } finally {
        if (budgetSyncTokenRef.current === token) setBudgetSyncing(false);
      }
    }, immediate ? 0 : 600);
  }, []);

  // `meta` (usually the previous draft) is spread before `result` so its
  // stale stats/trees don't clobber the freshly recomputed values.
  // `meta.orgRootIds` (Current Org only — see startFromExistingOrg) rides
  // along on every draft update via that same spread, so every later edit
  // keeps building the real-CEO-rooted tree without having to re-fetch it.
  const recompute = useCallback(
    (roles, design, newRoles, rationale, meta) => {
      const result = buildOrgDesignResult(roles, design, newRoles, rationale, undefined, meta?.orgRootIds);
      const nextDraft = { ...meta, roles, newRoles, ...result };
      setDraft(nextDraft);
      return nextDraft;
    },
    [setDraft]
  );

  // Returns whether the load succeeded so the landing page only navigates
  // into the workspace on success. `claimLoad`/`isActiveLoad` guard against
  // this resolving after the user has since abandoned it for a different
  // flow (PAIC import, opening a saved version) — an old request can never
  // overwrite a newer/different draft.
  const startFromExistingOrg = useCallback(async () => {
    setStartingExistingOrg(true);
    const loadToken = useOrgDesignStore.getState().claimLoad();
    try {
      // Fetched together (not sequentially) so pulling the real hierarchy
      // out of organization-view doesn't add a second round-trip's worth of
      // wait on top of the structure fetch. A failure here is swallowed —
      // organization-view is still only advisory for the warnings toast
      // below, and now additionally supplies the tree's root(s); either way,
      // its failure shouldn't block loading the workspace. On failure
      // resolveOrgViewRootIds yields an empty array, and the tree still roots
      // on every manager-less role (buildOrgForestFromOrphanRoots) — no path
      // ever surfaces a function name as the top of the org chart.
      const [response, view] = await Promise.all([
        getOrgRedesignCurrentOrgStructure(email),
        getOrgRedesignOrganizationView(email).catch((viewError) => {
          console.error("Error getting organization view:", viewError);
          return null;
        }),
      ]);
      if (!useOrgDesignStore.getState().isActiveLoad(loadToken)) return false;
      const rows = Array.isArray(response) ? response : [];
      const jobIdToUniqueId = new Map(rows.map((r) => [r.job_id, r.sb_unique_id]));
      const roles = rows.map((row) => mapCurrentOrgStructureRole(row, jobIdToUniqueId));

      // organization-view's roots[] are the real top-of-org role(s) (e.g.
      // the CEO) — see resolveOrgViewRootIds.
      const orgRootIds = resolveOrgViewRootIds(view, jobIdToUniqueId);

      recompute(roles, {}, [], "", {
        entryOption: ENTRY_OPTION.EXISTING_ORG,
        sourceLabel: "",
        baseVersionId: null,
        baseline_budget: computeBudget(roles, {}, []),
        targetBudget: null,
        publishedStatus: null,
        orgRootIds,
        sessionId: loadToken,
      });

      // `view.warnings.length` counts only roles with a directly broken/missing
      // manager_id — it doesn't include their descendants, which also won't
      // render (buildOrgForestFromRealRoots leaves the whole subtree out, not
      // just the directly-broken role — see its own comment). The wording
      // below is deliberately non-committal about the total affected count.
      if (view?.warnings?.length) {
        SbNotification.warning(
          `${view.warnings.length} reporting-line issue${view.warnings.length === 1 ? "" : "s"} found`,
          "Some affected roles or reporting branches may not appear in the org chart."
        );
      }

      return true;
    } catch (error) {
      if (useOrgDesignStore.getState().isActiveLoad(loadToken)) {
        SbNotification.error("Unable to load current org structure", "Please try again or check your access.");
      }
      return false;
    } finally {
      setStartingExistingOrg(false);
    }
  }, [recompute, email]);

  // `generated` arrives in one of two shapes: a fresh useGenerateOrgDesign()
  // result (snake_case new_roles, sourceVersionId) or a previously-saved
  // draft snapshot ("Open Existing", already camelCase newRoles/
  // orgRedesignId) — reading both keys means neither caller needs to know
  // which shape it has.
  const startFromPaicGenerated = useCallback(
    (sourceLabel, generated, sbSelectionUniqueId) => {
      const newRoles = generated.newRoles ?? generated.new_roles ?? [];
      const orgRedesignId = generated.orgRedesignId ?? generated.sourceVersionId ?? null;
      // Claims the load token too — an already-resolved result being applied
      // here should still supersede any slower startFromExistingOrg/
      // openVersion attempt still in flight elsewhere.
      const loadToken = useOrgDesignStore.getState().claimLoad();
      setDraft({
        roles: generated.roles,
        newRoles,
        before_tree: generated.before_tree,
        after_tree: generated.after_tree,
        design: generated.design,
        org_design_rationale: generated.org_design_rationale,
        stats: generated.stats,
        status_counts: generated.status_counts,
        projected_budget: generated.projected_budget,
        entryOption: ENTRY_OPTION.PAIC_RECOMMENDED,
        sourceLabel,
        sbSelectionUniqueId: sbSelectionUniqueId || null,
        baseVersionId: null,
        orgRedesignId,
        baseline_budget: generated.baseline_budget ?? computeBudget(generated.roles, {}, []),
        targetBudget: null,
        publishedStatus: generated.publishedStatus ?? null,
        sessionId: loadToken,
      });
    },
    [setDraft]
  );

  const updateHeadcount = useCallback(
    (roleId, newHc) => {
      if (!draft) return;
      const existing = draft.design[roleId] || {};
      const before = draft.roles.find((r) => r.sb_unique_id === roleId)?.total_positions ?? 0;
      let status;
      if (newHc === before) {
        // The only place that resets a status to Unchanged based on
        // headcount matching baseline — see normalizeDesign for why that
        // rule doesn't belong in the generic tree engine.
        status = "UNCHANGED";
      } else {
        const inferredStatus = newHc > before ? "AI AUGMENTED" : "REDUCED";
        // Don't override a status the user already picked explicitly via
        // the Status column.
        const hasExplicitOverride = existing.status && existing.status !== "UNCHANGED" && existing.status !== "REDUNDANT";
        status = hasExplicitOverride ? existing.status : inferredStatus;
      }
      const design = { ...draft.design, [roleId]: { ...existing, after_hc: newHc, status } };
      const nextDraft = recompute(draft.roles, design, draft.newRoles, draft.org_design_rationale, draft);
      scheduleBudgetSync(nextDraft);
    },
    [draft, recompute, scheduleBudgetSync]
  );

  const updateStatus = useCallback(
    (roleId, status) => {
      if (!draft) return;
      const role = draft.roles.find((r) => r.sb_unique_id === roleId);
      const existing = draft.design[roleId] || {};
      const beforeHc = existing.after_hc ?? role?.total_positions ?? 0;
      const afterHc = status === "REDUNDANT" ? 0 : beforeHc;
      const design = { ...draft.design, [roleId]: { ...existing, status, after_hc: afterHc } };
      const nextDraft = recompute(draft.roles, design, draft.newRoles, draft.org_design_rationale, draft);
      if (afterHc !== beforeHc) {
        scheduleBudgetSync(nextDraft);
      } else {
        // A pure status relabel — headcount didn't move, so don't let
        // recompute()'s local computeBudget() (which knows nothing about
        // status) overwrite the last real Projected Budget with a drifted
        // local estimate.
        setDraft({ ...nextDraft, projected_budget: draft.projected_budget });
      }
    },
    [draft, recompute, scheduleBudgetSync, setDraft]
  );

  const updateReportingLine = useCallback(
    (roleId, newManagerId, externalManager) => {
      if (!draft) return;
      const managerFields = buildManagerFields(draft, newManagerId, externalManager);
      const roles = draft.roles.map((r) => (r.sb_unique_id === roleId ? { ...r, ...managerFields } : r));
      // A reporting-line move alone doesn't touch status/after_hc, but it
      // still needs a `design` entry — buildExistingRoleUpdates (below) uses
      // that map to know which roles to send on Save Version, and a move
      // with no other change would otherwise leave no trace anywhere.
      const design = { ...draft.design, [roleId]: { ...(draft.design[roleId] || {}) } };
      recompute(roles, design, draft.newRoles, draft.org_design_rationale, draft);
    },
    [draft, recompute]
  );

  const renameRole = useCallback(
    (roleId, newTitle) => {
      if (!draft) return;
      const roles = draft.roles.map((r) => (r.sb_unique_id === roleId ? { ...r, job_title: newTitle } : r));
      recompute(roles, draft.design, draft.newRoles, draft.org_design_rationale, draft);
    },
    [draft, recompute]
  );

  // Shared by Org View's "Add Child Role" and Table View's "Add Role" — a
  // user-added role lives in the same newRoles array as an AI-proposed one.
  const addRole = useCallback(
    ({ title, managerId, headcount, microtaskFunction, annualCost }) => {
      if (!draft) return;
      const newRoles = [
        ...draft.newRoles,
        {
          id: generateNewRoleId(),
          role_title: title,
          status: "CREATED",
          after_hc: headcount || 1,
          manager_id: managerId,
          microtask_function: microtaskFunction,
          // `|| 140000` would also override a genuine $0 cost the user set —
          // only default when the field is actually missing. And only
          // default at all if this user can see the budget field in the
          // first place — otherwise `annualCost` is whatever stale value
          // sits in local state behind a hidden input, not something the
          // user actually chose. Left `undefined` (not the field's own
          // default), so it's dropped from any request body entirely
          // rather than sent as a fabricated real number or an explicit
          // null the server may not expect.
          annual_cost_usd_per_fte: canViewBudget ? (annualCost != null ? annualCost : 140000) : undefined,
          rationale: "Added manually in the workspace.",
        },
      ];
      const nextDraft = recompute(draft.roles, draft.design, newRoles, draft.org_design_rationale, draft);
      scheduleBudgetSync(nextDraft);
    },
    [draft, recompute, scheduleBudgetSync, canViewBudget]
  );

  const removeRole = useCallback(
    (roleId) => {
      if (!draft) return;
      updateStatus(roleId, "REDUNDANT");
    },
    [draft, updateStatus]
  );

  const removeNewRole = useCallback(
    (roleId) => {
      if (!draft) return;
      const newRoles = draft.newRoles.filter((r) => r.id !== roleId);
      const nextDraft = recompute(draft.roles, draft.design, newRoles, draft.org_design_rationale, draft);
      scheduleBudgetSync(nextDraft);
    },
    [draft, recompute, scheduleBudgetSync]
  );

  const updateNewRoleHeadcount = useCallback(
    (roleId, newHc) => {
      if (!draft) return;
      const newRoles = draft.newRoles.map((r) => (r.id === roleId ? { ...r, after_hc: newHc } : r));
      const nextDraft = recompute(draft.roles, draft.design, newRoles, draft.org_design_rationale, draft);
      scheduleBudgetSync(nextDraft);
    },
    [draft, recompute, scheduleBudgetSync]
  );

  const updateNewRoleManager = useCallback(
    (roleId, newManagerId, externalManager) => {
      if (!draft) return;
      const managerFields = buildManagerFields(draft, newManagerId, externalManager);
      const newRoles = draft.newRoles.map((r) => (r.id === roleId ? { ...r, ...managerFields } : r));
      recompute(draft.roles, draft.design, newRoles, draft.org_design_rationale, draft);
    },
    [draft, recompute]
  );

  const findNewRoleByNodeId = useCallback(
    (nodeId) => (draft?.newRoles || []).find((r) => newRoleNodeId(r.id) === nodeId) || null,
    [draft]
  );

  const setTargetBudget = useCallback(
    (value) => {
      if (!draft) return;
      const nextDraft = { ...draft, targetBudget: value };
      setDraft(nextDraft);
      scheduleBudgetSync(nextDraft);
    },
    [draft, setDraft, scheduleBudgetSync]
  );

  // Every Org Redesign version is published immediately on creation (no
  // draft state exists server-side for either source type — see Org
  // Redesign API doc §5/§11) and PUT /version/{id}/update itself has no
  // published_status check, so there's nothing real to guard against here:
  // edits after the first save are the expected, ongoing workflow, not an
  // exception.
  // Returns { entry, serverSynced } — callers should only show a "saved"
  // success toast when serverSynced is true, since the local snapshot
  // (`entry`) is created unconditionally and would otherwise look like a
  // success even when the server sync warning fired right alongside it.
  const saveVersion = useCallback(
    async (label) => {
      let serverSynced = false;
      if (draft) {
        // A manager-id cycle (A reports to B, B reports to A) makes the
        // affected roles vanish from the tree entirely — buildTree never
        // reaches them — while they still count toward budget/stats, which
        // read the flat roles list. Blocked here rather than left to ship a
        // chart that visually disagrees with its own totals.
        const unreachableIds = findUnreachableRoleIds(draft.roles, draft.newRoles);
        if (unreachableIds.size > 0) {
          const affected = draft.roles
            .filter((r) => unreachableIds.has(r.sb_unique_id))
            .map((r) => r.job_title)
            .concat(
              draft.newRoles.filter((nr) => unreachableIds.has(newRoleNodeId(nr.id))).map((nr) => nr.role_title)
            );
          SbNotification.error(
            "Can't save — reporting-line cycle detected",
            `${affected.join(", ")} report to each other in a loop. Fix the reporting line before saving.`
          );
          return { entry: saveDraftAsVersion(label), serverSynced };
        }

        // reports_to only accepts a real JD's sb_unique_id — the dropdowns no
        // longer offer proposed roles, but an older draft may still carry one.
        const reportingToProposed = findRolesReportingToProposedRoles(draft.roles, draft.newRoles);
        if (reportingToProposed.length > 0) {
          SbNotification.error(
            "Can't save — reports to a proposed role",
            `${reportingToProposed.map((r) => r.job_title).join(", ")} report${reportingToProposed.length === 1 ? "s" : ""} to a proposed role, which can't be saved as a manager yet. Choose an existing role or No manager.`
          );
          return { entry: saveDraftAsVersion(label), serverSynced };
        }

        const isCurrentOrg = draft.entryOption !== ENTRY_OPTION.PAIC_RECOMMENDED;
        const existingRoles = buildExistingRoleUpdates(draft);
        const newRoles = buildNewRoleUpdates(draft);
        const isFirstSave = !draft.orgRedesignId;
        const orgRedesignName = label?.trim() || undefined;
        // A PAIC version already exists by the time the workspace saves it
        // (created on Generate), so its name goes on the update call instead.
        const sendsPaicName = !isCurrentOrg && Boolean(orgRedesignName);

        // A brand-new CURRENT ORG version requires existing_roles/new_roles
        // right in the create body (doc §5) — with nothing edited yet, that
        // call would 400 no matter how many times it's retried.
        if (isFirstSave && isCurrentOrg && !existingRoles.length && !newRoles.length) {
          SbNotification.warning("Nothing to save yet", "Make at least one change before saving a new version.");
          return { entry: saveDraftAsVersion(label), serverSynced };
        }

        // Checked up front, matching what each call is actually gated on
        // server-side (org_redesign:create for the first save,
        // org_redesign:edit for the follow-up role-edits call — which a
        // PAIC first save also needs, since PAIC's create call never
        // accepts role edits itself), so a user missing one doesn't get a
        // silent no-op or a raw 403 — they get the same "couldn't sync"
        // messaging as any other server failure.
        const willNeedUpdateCall = (existingRoles.length || newRoles.length || sendsPaicName) && (!isCurrentOrg || !isFirstSave);
        if (isFirstSave && !canCreateOrgRedesign) {
          SbNotification.warning("Server sync failed", "You don't have permission to create a new Organisation Design version — your edits stay in the workspace for now.");
          return { entry: saveDraftAsVersion(label), serverSynced };
        }
        if (willNeedUpdateCall && !canUpdateOrgRedesign) {
          SbNotification.warning("Server sync failed", "You don't have permission to update this Organisation Design version — your edits stay in the workspace for now.");
          return { entry: saveDraftAsVersion(label), serverSynced };
        }

        try {
          let orgRedesignId = draft.orgRedesignId;
          // The row of the version that ends up holding these edits — update
          // creates a new iteration (new id + version) rather than editing
          // in place, so the draft has to move onto it.
          let savedRow = null;
          if (isFirstSave) {
            // The Save Version modal's label is the only place a CURRENT ORG
            // draft gets a name from — the create endpoint reads that name
            // off org_redesign_name (project_name is PAIC-only and always
            // None for this source type), so it must be sent under that key
            // or the version is created nameless.
            const created = await createOrgRedesignVersion(
              isCurrentOrg
                ? { source_type: "CURRENT ORG", org_redesign_name: orgRedesignName, existing_roles: existingRoles, new_roles: newRoles }
                : { source_type: "PAIC", sb_selection_unique_id: draft.sbSelectionUniqueId || "" },
              refreshToken
            );
            orgRedesignId = created?.sb_org_redesign_unique_id || null;
            savedRow = created;
          }

          // For CURRENT ORG, the create call above already applied
          // existing_roles/new_roles directly — an immediate follow-up
          // update with the identical payload would just be a redundant
          // no-op call. Still needed for every PAIC save (create never
          // accepts role edits on that branch) and for any later CURRENT
          // ORG save once the version already exists.
          const needsUpdateCall = orgRedesignId && willNeedUpdateCall;
          if (needsUpdateCall) {
            const updated = await updateOrgRedesignVersion(orgRedesignId, {
              existing_roles: existingRoles,
              new_roles: newRoles,
              ...(sendsPaicName && { org_redesign_name: orgRedesignName }),
            });
            if (updated?.sb_org_redesign_unique_id) {
              orgRedesignId = updated.sb_org_redesign_unique_id;
              savedRow = updated;
            }
          }
          // versionLabel only drives the header/PDF — sourceLabel stays put,
          // since it's also findExisting's lookup key (usePaicOrgDesignLookup).
          // Read fresh rather than from the `draft` closure — a budget sync may
          // have landed during the awaits above — and skipped entirely if the
          // user has since loaded a different draft.
          const latestDraft = useOrgDesignStore.getState().draft;
          const versionLabel = savedRow?.version != null ? buildVersionLabel(savedRow, orgRedesignName) : latestDraft?.versionLabel;
          if (
            latestDraft &&
            latestDraft.sessionId === draft.sessionId &&
            (orgRedesignId !== latestDraft.orgRedesignId || versionLabel !== latestDraft.versionLabel)
          ) {
            setDraft({ ...latestDraft, orgRedesignId, versionLabel });
          }
          serverSynced = true;
        } catch (error) {
          console.error("Error saving Org Redesign version:", error);
          SbNotification.warning("Server sync failed", getOrgRedesignSaveErrorMessage(error));
        }
      }
      return { entry: saveDraftAsVersion(label), serverSynced };
    },
    [draft, saveDraftAsVersion, refreshToken, setDraft, canCreateOrgRedesign, canUpdateOrgRedesign]
  );

  return {
    draft,
    startingExistingOrg,
    budgetSyncing,
    scheduleBudgetSync,
    startFromExistingOrg,
    startFromPaicGenerated,
    updateHeadcount,
    updateStatus,
    updateReportingLine,
    renameRole,
    addRole,
    removeRole,
    removeNewRole,
    updateNewRoleHeadcount,
    updateNewRoleManager,
    findNewRoleByNodeId,
    setTargetBudget,
    saveVersion,
  };
};

// No real "already generated?" lookup endpoint exists — checks the
// locally-saved version list for a matching sourceLabel instead.
export const usePaicOrgDesignLookup = () => {
  const { versions } = useOrgDesignVersions();
  const findExisting = useCallback(
    (sourceLabel) => versions.find((v) => v.entryOption === ENTRY_OPTION.PAIC_RECOMMENDED && v.sourceLabel === sourceLabel) || null,
    [versions]
  );
  return { findExisting };
};

// The cloud job takes ~2 minutes just to initiate then runs for an unbounded
// duration. Rather than auto-polling in the background indefinitely (which
// has no way to tell "still running" apart from "backend job died" without
// a documented failure status to check for), this waits out the initiation
// delay, checks ONCE, and if still not ready, stops there — the caller shows
// a manual "Check Status" action for the user to recheck on demand. Same
// pattern the PAIC dashboard already uses for its own background jobs: the
// group dropdown re-fetches status on open rather than polling in a loop.
const GENERATE_INITIATION_DELAY_MS = 2 * 60 * 1000;

// `current_org_structure` is populated immediately on creation (it's just
// today's org, no AI work needed) while `new_org_structure` is still being
// generated — so checking either object for truthiness alone reports ready
// long before the job actually finishes, and the still-empty
// new_org_structure then adapts into a blank "nothing changed" design
// instead of a real result. `status` is the one field the backend commits to
// updating only once the job is actually done, so it's the sole source of
// truth here.
const isOrgRedesignRowReady = (row) => row?.status === "Completed";

const abortableDelay = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

// One GET, one readiness check, no retry of its own — used both by generate()
// for its single post-delay check and by the caller's on-demand "Check
// Status" recheck once generation is still pending.
const checkOrgRedesignReady = async (orgRedesignId, signal) => {
  const row = await getOrgRedesignVersion(orgRedesignId, signal);
  if (!isOrgRedesignRowReady(row)) return null;
  return { ...adaptVersionRowToDraft(row), sourceVersionId: orgRedesignId };
};

export const useGenerateOrgDesign = () => {
  const [generating, setGenerating] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  // Resolves to the ready draft, or to `{ pending: true, orgRedesignId }`
  // if the job is still running after the initial wait — callers must
  // handle both shapes (see PaicRecommendedImport.jsx's handleGenerate).
  const generate = useCallback(async (sbSelectionUniqueId, refreshToken) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setGenerating(true);
    try {
      const created = await createOrgRedesignVersion(
        { source_type: "PAIC", sb_selection_unique_id: sbSelectionUniqueId || "" },
        refreshToken,
        controller.signal
      );
      const orgRedesignId = created?.sb_org_redesign_unique_id || null;
      if (isOrgRedesignRowReady(created)) {
        return { ...adaptVersionRowToDraft(created), sourceVersionId: orgRedesignId };
      }
      await abortableDelay(GENERATE_INITIATION_DELAY_MS, controller.signal);
      const ready = await checkOrgRedesignReady(orgRedesignId, controller.signal);
      return ready || { pending: true, orgRedesignId };
    } finally {
      // Only touch shared state if this is still the active controller —
      // a second generate() may have already started and reassigned it.
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setGenerating(false);
      }
    }
  }, []);

  // The manual "Check Status" recheck — one GET, no signal of its own since
  // there's nothing long-running here to cancel.
  const checkStatus = useCallback((orgRedesignId) => checkOrgRedesignReady(orgRedesignId), []);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return { generate, generating, cancel, checkStatus };
};
